import axios from "axios";

function getSubdomain(): string | null {
    if (typeof window === 'undefined') return null;
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || /^[\d.]+$/.test(hostname)) return null;
    if (hostname.endsWith('.localhost')) return hostname.slice(0, -'.localhost'.length);
    const appDomain = process.env.NEXT_PUBLIC_APP_DOMAIN || 'mgtruck.local';
    if (hostname.endsWith('.' + appDomain)) return hostname.slice(0, -(appDomain.length + 1));
    return null;
}

const api = axios.create({
    baseURL: "/api",
    headers: { "Content-Type": "application/json" },
    withCredentials: true,
});

api.interceptors.request.use((config) => {
    const token =
        (typeof window !== "undefined" && localStorage.getItem("token")) ||
        (typeof document !== "undefined" &&
            document.cookie.match(/(?:^|;\s*)token=([^;]+)/)?.[1]);
    if (token) config.headers.Authorization = `Bearer ${token}`;

    // Hint subdomain ke backend untuk routing (otoritas tetap di klaim JWT)
    const sub = getSubdomain();
    if (sub) config.headers['x-tenant-subdomain'] = sub;

    // Global Date Validation (BUG-148)
    const validateDateRange = (startStr?: string, endStr?: string) => {
        if (!startStr && !endStr) return null;
        
        const checkExtreme = (dateStr: string, label: string) => {
            const year = new Date(dateStr).getFullYear();
            if (year < 2000 || year > 2100) {
                return `Tahun pada ${label} (${year}) tidak valid. Masukkan tahun yang wajar (2000 - 2100).`;
            }
            return null;
        };

        if (startStr) {
            const err = checkExtreme(startStr, "Tanggal Awal");
            if (err) return err;
        }
        
        if (endStr) {
            const err = checkExtreme(endStr, "Tanggal Akhir");
            if (err) return err;
        }

        if (startStr && endStr) {
            const start = new Date(startStr);
            const end = new Date(endStr);
            
            if (start > end) {
                return "Tanggal Awal tidak boleh lebih besar dari Tanggal Akhir.";
            }
            
            const diffDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
            if (diffDays > 366) {
                return "WARNING_RANGE_TOO_LONG";
            }
        }
        return null;
    };

    const err = 
        validateDateRange(config.params?.tglAwal, config.params?.tglAkhir) ||
        validateDateRange(config.params?.startDate, config.params?.endDate) ||
        validateDateRange(config.data?.tglAwal, config.data?.tglAkhir) ||
        validateDateRange(config.data?.startDate, config.data?.endDate);

    if (err) {
        if (err === "WARNING_RANGE_TOO_LONG") {
            const proceed = window.confirm("Range tanggal lebih dari 1 tahun. Proses pencarian data mungkin membutuhkan waktu yang cukup lama. Lanjutkan?");
            if (!proceed) {
                return Promise.reject(new axios.Cancel("Pencarian dibatalkan oleh pengguna (range terlalu panjang)."));
            }
        } else {
            // Import toast dynamically to avoid circular dependencies or hydration issues if needed
            // but in interceptor it's usually fine
            import("react-hot-toast").then((module) => {
                module.default.error(err);
            });
            return Promise.reject(new axios.Cancel(err));
        }
    }

    return config;
});

api.interceptors.response.use(
    (res) => res,
    (err) => {
        if (typeof window !== "undefined") {
            if (err.response?.status === 401) {
                window.location.href = "/login";
            } else if (
                err.response?.status === 403 &&
                err.response?.data?.code === "TENANT_INACTIVE"
            ) {
                // Tenant di-suspend saat sesi berjalan → beri tahu LayoutWrapper untuk blokir.
                window.dispatchEvent(new CustomEvent("tenant-inactive"));
            } else if (
                err.response?.status === 403 &&
                err.response?.data?.code === "FEATURE_NOT_IN_PLAN"
            ) {
                // Fitur di luar paket langganan → pesan ramah + ajakan upgrade (tanpa redirect).
                const msg =
                    err.response?.data?.message ||
                    "Fitur ini tidak termasuk paket langganan Anda. Upgrade paket untuk mengaksesnya.";
                import("react-hot-toast").then((m) => m.default.error(msg, { duration: 5000 }));
            } else if (
                err.response?.status === 403 &&
                err.response?.data?.code === "QUOTA_EXCEEDED"
            ) {
                // Kuota paket (user/kendaraan) penuh → pesan ramah + ajakan upgrade.
                const msg =
                    err.response?.data?.message ||
                    "Kuota paket Anda sudah penuh. Upgrade paket untuk menambah data.";
                import("react-hot-toast").then((m) => m.default.error(msg, { duration: 5000 }));
            }
        }
        return Promise.reject(err);
    }
);

export default api;
